import AttachmentIcon from '@mui/icons-material/Attachment'
import DeleteIcon from '@mui/icons-material/Delete'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import ForumIcon from '@mui/icons-material/Forum'
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import React from 'react'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import GrantContent from '../../../components/GrantContent'
import type { EstadoCivil, Genero, TipoPersona } from '../../../model/kartera-api-model'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import * as Permisos from '../../../utils/Permisos'
import { useCustomerFormModel } from './customer-form-model'
import type { CustomerFormProps } from './customer-form-model'
import CustomerDrawer from '../drawer/customer-drawer'

const outlineBtnSx = {
    width: 'auto',
    minWidth: 'auto',
    flex: 'none',
    height: 40,
    padding: '0 16px',
    borderRadius: '12px',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '14px',
    lineHeight: 1,
    backgroundColor: 'rgb(var(--k-surface))',
    color: 'rgb(var(--k-ink))',
    border: '1px solid rgb(var(--k-line))',
    '& .MuiButton-startIcon': { color: 'inherit' },
    '&:hover': {
        backgroundColor: 'rgb(var(--k-hover))',
        border: '1px solid rgb(var(--k-line))',
        color: 'rgb(var(--k-ink))',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const dangerBtnSx = {
    ...outlineBtnSx,
    backgroundColor: 'rgb(var(--k-danger))',
    color: '#fff',
    border: 0,
    '&:hover': {
        backgroundColor: 'rgb(var(--k-danger))',
        filter: 'brightness(1.08)',
        boxShadow: 'none',
    },
} satisfies SxProps<Theme>

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }
const ColoniaLoadingIcon = ({ className }: { className?: string }) => <CircularProgress className={className} size={18} />

const CustomerForm = (props: CustomerFormProps) => {
    const model = useCustomerFormModel(props)
    const { form, change } = model

    return (
        <KarteraAdminTemplate loading={false} modal={props.modal} title={model.title}>
            {model.entityId && (
                <CustomerDrawer
                    clienteId={model.panel ? model.entityId : null}
                    initialTab={model.panel || 'general'}
                    onClose={model.closePanel}
                    onEdit={model.closePanel}
                />
            )}

            <Dialog open={model.confirmDelete} onClose={model.cancelDelete} slotProps={{ paper: { className: 'k-dialog' } }}>
                <DialogTitle>Eliminar cliente</DialogTitle>
                <DialogContent>
                    <p className="m-0 text-sm text-k-ink-2">¿Desea eliminar al cliente? Si continúa, se perderá toda la información del cliente, la bitácora y los adjuntos.</p>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={outlineBtnSx} onClick={model.cancelDelete}>Cancelar</Button>
                    <Button variant="contained" sx={dangerBtnSx} onClick={model.deleteCliente} disabled={model.deleting}>Eliminar</Button>
                </DialogActions>
            </Dialog>

            <form onSubmit={model.save} className="relative mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                {model.busy && (
                    <div className="absolute inset-0 z-40 flex items-start justify-center rounded-2xl bg-k-bg/80 pt-24" role="status">
                        <div className="flex flex-col items-center gap-3 rounded-2xl border border-k-line bg-k-surface px-8 py-6 shadow-k-pop">
                            <RefreshIcon className="animate-spin text-brand-indigo" />
                            <p className="m-0 text-sm font-semibold text-k-ink">{model.busyLabel}</p>
                        </div>
                    </div>
                )}

                <KarteraPageHeader
                    title={model.title}
                    description={model.isEdit ? 'Actualiza los datos del cliente en tu cartera.' : 'Captura rápida: solo los datos necesarios para registrarlo en tu cartera.'}
                    backTo={model.backTo}
                    backLabel="Clientes"
                    actions={model.isEdit ? (
                        <div className="flex flex-wrap gap-2">
                            <GrantContent permisos={[Permisos.ADJUNTOS_R]}>
                                <Button type="button" variant="outlined" startIcon={<AttachmentIcon />} sx={outlineBtnSx} onClick={() => model.openPanel('adjuntos')}>
                                    Adjuntos
                                </Button>
                            </GrantContent>
                            <GrantContent permisos={[Permisos.BITACORA_R]}>
                                <Button type="button" variant="outlined" startIcon={<ForumIcon />} sx={outlineBtnSx} onClick={() => model.openPanel('bitacora')}>
                                    Bitácora
                                </Button>
                            </GrantContent>
                            {model.activo && (
                                <GrantContent permisos={[Permisos.CLIENTES_D]}>
                                    <Button type="button" variant="contained" startIcon={<DeleteIcon />} sx={dangerBtnSx} onClick={model.askDelete} disabled={model.deleting}>
                                        Eliminar
                                    </Button>
                                </GrantContent>
                            )}
                        </div>
                    ) : (
                        <span className="text-[13px] text-k-muted"><span className="text-k-danger">*</span> Obligatorio</span>
                    )}
                />

                {model.message && (
                    <div className={`flex items-center gap-3 rounded-[14px] px-4 py-3.5 text-sm ${model.message.type === 'error' ? 'bg-k-danger-soft text-k-danger-ink' : 'bg-k-ok-soft text-k-ok'}`}>
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.message.message}</p>
                    </div>
                )}

                <section className="k-card">
                    <h2 className="k-card-title">Datos generales</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            label="Personalidad"
                            value={form.personalidad}
                            helperText="Persona física o moral."
                            onChange={(event) => change.personalidad(event.target.value as TipoPersona)}
                            slotProps={{ select: selectMenu }}
                        >
                            <MenuItem value="fisica">Física</MenuItem>
                            <MenuItem value="moral">Moral</MenuItem>
                        </TextField>
                    </div>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        {model.esFisica ? (
                            <>
                                <TextField className="v2-field" variant="filled" fullWidth required label="Nombre(s)" value={form.nombres} onChange={(event) => change.nombres(event.target.value)} />
                                <TextField className="v2-field" variant="filled" fullWidth required label="Apellido paterno" value={form.apPat} onChange={(event) => change.apPat(event.target.value)} />
                                <TextField className="v2-field" variant="filled" fullWidth label="Apellido materno" value={form.apMat} onChange={(event) => change.apMat(event.target.value)} />
                                <TextField
                                    className="v2-field md:col-span-2" variant="filled" fullWidth
                                    required
                                    label="Alias"
                                    value={form.alias}
                                    onChange={(event) => change.alias(event.target.value)}
                                    slotProps={{
                                        input: {
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Apodo o nombre corto con el que se dirige al cliente">
                                                        <HelpOutlineIcon className="text-k-muted-2" style={{ fontSize: 18 }} />
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    label="Nacimiento"
                                    type="date"
                                    value={model.fecNacimientoValue}
                                    onChange={(event) => change.fecNacimiento(event.target.value)}
                                />
                                <TextField
                                    className="v2-field v2-select" variant="filled" fullWidth
                                    select
                                    required
                                    label="Sexo"
                                    value={form.sexo}
                                    onChange={(event) => change.sexo(event.target.value as Genero | '')}
                                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    <MenuItem value="hombre">Hombre</MenuItem>
                                    <MenuItem value="mujer">Mujer</MenuItem>
                                </TextField>
                                <TextField
                                    className="v2-field" variant="filled" fullWidth
                                    required
                                    label="RFC"
                                    value={form.rfc}
                                    onChange={(event) => change.rfc(event.target.value)}
                                />
                                <TextField
                                    className="v2-field v2-select" variant="filled" fullWidth
                                    select
                                    label="Estado civil"
                                    value={form.edoCivil}
                                    onChange={(event) => change.edoCivil(event.target.value as EstadoCivil | '')}
                                    slotProps={{ select: { displayEmpty: true, ...selectMenu } }}
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    <MenuItem value="soltero">Soltero</MenuItem>
                                    <MenuItem value="casado">Casado</MenuItem>
                                    <MenuItem value="viudo">Viudo</MenuItem>
                                    <MenuItem value="divorciado">Divorciado</MenuItem>
                                    <MenuItem value="concubinato">Concubinato</MenuItem>
                                </TextField>
                            </>
                        ) : (
                            <>
                                <TextField className="v2-field md:col-span-2" variant="filled" fullWidth required label="Razón social" value={form.nombres} onChange={(event) => change.nombres(event.target.value)} />
                                <TextField className="v2-field md:col-span-2" variant="filled" fullWidth required label="Alias" helperText="Nombre corto para correos y comunicados." value={form.alias} onChange={(event) => change.alias(event.target.value)} />
                                <TextField className="v2-field" variant="filled" fullWidth required label="RFC" value={form.rfc} onChange={(event) => change.rfc(event.target.value)} />
                            </>
                        )}
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Domicilio</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField className="v2-field md:col-span-3" variant="filled" fullWidth required label="Calle y número" value={form.direccion} onChange={(event) => change.direccion(event.target.value)} />
                        <TextField className="v2-field" variant="filled" fullWidth required label="Código postal" helperText="Con 5 dígitos se cargan las colonias." value={form.cp} onChange={(event) => change.cp(event.target.value)} />
                        <TextField
                            className="v2-field v2-select" variant="filled" fullWidth
                            select
                            required
                            disabled={model.loadingColonias}
                            label="Colonia"
                            value={form.colonia}
                            helperText={model.loadingColonias ? 'Cargando colonias…' : undefined}
                            onChange={(event) => change.colonia(event.target.value)}
                            slotProps={{
                                select: {
                                    displayEmpty: true,
                                    ...selectMenu,
                                    IconComponent: model.loadingColonias ? ColoniaLoadingIcon : undefined,
                                },
                            }}
                        >
                            <MenuItem value=""><em>{model.loadingColonias ? 'Cargando…' : 'Seleccione'}</em></MenuItem>
                            {model.codigos.map((codigo) => (
                                <MenuItem key={codigo.id} value={codigo.id ?? ''}>{codigo.asentamiento}</MenuItem>
                            ))}
                        </TextField>
                        <TextField className="v2-field" variant="filled" fullWidth disabled label="Municipio" helperText="Se llena con la colonia seleccionada." value={model.municipio} />
                    </div>
                </section>

                <section className="k-card">
                    <h2 className="k-card-title">Localización</h2>
                    <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 md:grid-cols-3">
                        <TextField className="v2-field md:col-span-2" variant="filled" fullWidth type="email" label="Correo" value={form.correo} onChange={(event) => change.correo(event.target.value)} />
                        <div className="v2-phone">
                            <span className="v2-phone-label">Celular<span>*</span></span>
                            <PhoneInput
                                country="mx"
                                preferredCountries={['mx', 'us']}
                                disableDropdown
                                specialLabel=""
                                value={form.celular}
                                onChange={(value) => change.celular(value)}
                                inputProps={{ required: true, name: 'celular' }}
                            />
                            <p className="v2-phone-hint">Número con el que se localiza al cliente.</p>
                        </div>
                    </div>
                </section>

                <div className="flex flex-wrap items-center justify-end gap-2.5 pt-1">
                    <button
                        type="button"
                        className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                        onClick={model.cancel}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={model.busy || model.loadingColonias}
                        className="inline-flex h-11 items-center gap-2 rounded-xl bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <CheckIcon />
                        Guardar cliente
                    </button>
                </div>
            </form>
        </KarteraAdminTemplate>
    )
}

export default CustomerForm
